import axiosInstance from '../utils/setupAxios';

// Admin API for the Apple team transfer (APPIXI-159), served by coin-api under
// /songs/admin/apple-transfer. The contract is section A of the OC-622 report:
//   GET  /summary                      -> { phase, hk_key_configured, counts, running_action, … }
//   GET  /users                        ?page&items_per_page&q&status&team&has_transfer_sub&has_error
//                                      -> { items, objects: { total }, counts }
//   POST /users/{id}/transfer          { action? } -> { result: { ok, status, error }, user, action_log }
//   GET  /run/preview                  ?scope&action? -> { action, scope, total }
//   POST /run                          { scope, action?, expected_total? } -> 202 { queued, action, action_log }
//   GET  /actions/{id}                 -> { action_log }
//   POST /actions/{id}/cancel          -> { action_log }
//   GET  /actions                      ?page&items_per_page&user_id -> { items, objects: { total } }
// Errors are { success: false, message: <code>, data } with the codes in ERROR_MESSAGES.
const BASE = '/songs/admin/apple-transfer';

export const STATUSES = ['pending', 'generated', 'exchanged', 'error', 'conflict'];

export const ERROR_MESSAGES = {
  forbidden: 'This account is not allowed to transfer Apple users.',
  user_not_found: 'Apple user not found.',
  action_not_found: 'Run not found.',
  nothing_to_do: 'There are no users to run.',
  hk_key_not_configured: 'The HK Apple key is not on the server yet.',
  missing_target_team_id: 'The HK Team ID is not configured.',
  validation_failed: 'Invalid parameters.',
  not_running: 'That run has already finished.',
  batch_running: 'Another run is in progress.',
  total_changed: 'The number of users changed; please confirm again.',
  apple_auth_failed: "Apple rejected the server's key (ask Tony to check the configuration).",
};

export const errorCode = (err) => err?.response?.data?.message;

export const describeError = (err, fallback = 'Request failed') => {
  const code = errorCode(err);
  if (code && ERROR_MESSAGES[code]) {
    const detail = err.response?.data?.data?.detail;
    return detail ? `${ERROR_MESSAGES[code]} (${detail})` : ERROR_MESSAGES[code];
  }
  return code || err?.message || fallback;
};

// Only a 404 without one of our error codes means the endpoints are not deployed yet;
// after deploy a 404 is user_not_found / action_not_found and must show as an error.
export const isNotDeployed = (err) => err?.response?.status === 404 && !ERROR_MESSAGES[errorCode(err)];

export function fetchAppleTransferSummary() {
  return axiosInstance.get(`${BASE}/summary`);
}

export function fetchAppleTransferUsers(params = {}) {
  return axiosInstance.get(`${BASE}/users`, { params });
}

// -> { user, actions } (the user's single-user actions, newest first, at most 20)
export function fetchAppleTransferUser(id) {
  return axiosInstance.get(`${BASE}/users/${id}`);
}

export function transferAppleUser(id, action) {
  return axiosInstance.post(`${BASE}/users/${id}/transfer`, action ? { action } : {});
}

// action is optional: without it the server uses the phase default (generate before the
// transfer, exchange after it).
export function previewAppleTransferBatch(scope, action) {
  return axiosInstance.get(`${BASE}/run/preview`, { params: action ? { scope, action } : { scope } });
}

export function runAppleTransferBatch(scope, expectedTotal, action) {
  return axiosInstance.post(`${BASE}/run`, action
    ? { scope, action, expected_total: expectedTotal }
    : { scope, expected_total: expectedTotal });
}

export function fetchAppleTransferAction(id) {
  return axiosInstance.get(`${BASE}/actions/${id}`);
}

export function cancelAppleTransferAction(id) {
  return axiosInstance.post(`${BASE}/actions/${id}/cancel`);
}

export function fetchAppleTransferActions(params = {}) {
  return axiosInstance.get(`${BASE}/actions`, { params });
}

// ---- Fake data (all values made up), shown only while the endpoints are not deployed ----

const FAKE_ERRORS = [
  'generate: invalid_request (HTTP 400)',
  'generate: invalid_client (HTTP 401)',
  'generate: http_429 (HTTP 429)',
];

// 32 hex characters, like the middle part of a real Apple sub.
const fakeHex = (i, salt) => Array.from({ length: 32 }, (_, k) => ((i * 7 + salt * 13 + k * 5) % 16).toString(16)).join('');

const fakeUsers = Array.from({ length: 57 }, (_, i) => {
  const id = 8000 + i * 7;
  const status = i < 30 ? 'pending' : i < 42 ? 'generated' : i < 48 ? 'exchanged' : i < 55 ? 'error' : 'conflict';
  const relay = i % 2 === 0;
  const hasSub = ['generated', 'exchanged', 'conflict'].includes(status);
  return {
    id,
    name: `songfake${String(i).padStart(3, '0')}`,
    email: relay ? `fake${i}x7k2@privaterelay.appleid.com` : `fake.user${i}@example.com`,
    is_private_email: relay,
    apple_id: `00${1000 + i}.${fakeHex(i, 1)}.0${100 + i}`,
    apple_sub_team: status === 'exchanged' ? 'hk' : 'vi',
    apple_sub_vi: status === 'exchanged' ? `00${2000 + i}.${fakeHex(i, 2)}.0${200 + i}` : null,
    apple_email_vi: status === 'exchanged' ? `fake${i}old@privaterelay.appleid.com` : null,
    apple_transfer_sub: hasSub ? `00${3000 + i}.${fakeHex(i, 3)}.0${300 + i}` : null,
    has_transfer_sub: hasSub,
    apple_migration_status: status,
    apple_migration_error: status === 'error'
      ? FAKE_ERRORS[i % FAKE_ERRORS.length]
      : status === 'conflict' ? `exchange: email_taken_by_user:${5000 + i}` : null,
    apple_migration_at: status === 'pending' ? null : `2026-10-30T0${i % 9}:1${i % 6}:00Z`,
    next_action: status === 'exchanged' || status === 'generated' ? null : 'generate',
  };
});

const fakeActions = [
  { id: 3, action: 'generate', scope: 'errors', status: 'done', admin_email: 'admin@example.com', target: 'errors', result: 'done 6 ok, 1 failed', created_at: '2026-10-30T09:20:00Z' },
  { id: 2, action: 'generate', scope: 'user', user_id: 8091, status: 'done', admin_email: 'admin@example.com', target: 'user 8091', result: 'generated', created_at: '2026-10-30T09:05:00Z' },
  { id: 1, action: 'generate', scope: 'user', user_id: 8364, status: 'done', admin_email: 'cli', target: 'user 8364', result: 'error: invalid_request (HTTP 400)', created_at: '2026-10-30T08:58:00Z' },
];

const countBy = (rows) => {
  const counts = { total: rows.length, with_transfer_sub: 0, team_vi: 0, team_hk: 0 };
  STATUSES.forEach((s) => { counts[s] = 0; });
  rows.forEach((u) => {
    counts[u.apple_migration_status] += 1;
    if (u.has_transfer_sub) counts.with_transfer_sub += 1;
    counts[`team_${u.apple_sub_team}`] += 1;
  });
  return counts;
};

export function fakeAppleTransferSummary() {
  return { phase: 'before_transfer', hk_key_configured: false, migration_hold: false, counts: countBy(fakeUsers), running_action: null };
}

export function fakeAppleTransferUsers(params = {}) {
  const { page = 1, items_per_page: perPage = 20, q, status, team, has_transfer_sub: hasSub, has_error: hasError } = params;
  const needle = (q || '').toLowerCase();
  const rows = fakeUsers.filter((u) =>
    (!needle || String(u.id) === needle || u.email.toLowerCase().includes(needle))
    && (!status || u.apple_migration_status === status)
    && (!team || u.apple_sub_team === team)
    && (hasSub === undefined || u.has_transfer_sub === (hasSub === '1'))
    && (!hasError || ['error', 'conflict'].includes(u.apple_migration_status)));
  return {
    items: rows.slice((page - 1) * perPage, page * perPage),
    objects: { total: rows.length },
    counts: countBy(fakeUsers),
  };
}

export function fakeAppleTransferPreview(scope, action = 'generate') {
  const counts = countBy(fakeUsers);
  const total = scope === 'errors' ? counts.error : scope === 'pending' ? counts.pending : counts.error + counts.pending;
  return { action, scope, total };
}

export function fakeAppleTransferUser(id) {
  const user = fakeUsers.find((u) => u.id === Number(id)) || null;
  return { user, actions: fakeActions.filter((a) => a.scope === 'user' && a.user_id === Number(id)) };
}

export function fakeAppleTransferActions() {
  return { items: fakeActions, objects: { total: fakeActions.length } };
}
